/**
 * 🔴 THE PANELS, IN THE ORDER THE STRIP DRAWS THEM — **ONE LIST, AND THE UNION IS DERIVED FROM
 * IT** (W30(j), 2026-09-19).
 *
 * It was a hand-written union beside a hand-written `Record` of labels whose `Object.keys` was
 * the order. That is two statements of one membership, and W30(j) needed a THIRD — the values a
 * pasted `?tab=` may hold. This repo's standing rule is to generate one side from the other
 * rather than type a list twice, so the array is the fact and `ClientTab` is read off it: a tab
 * added here gains its query value, its position and a compiler error in `TAB_LABEL` and in
 * `phoneGroup` until it is given a word.
 */
/* 🔴 W72 STEP 2 — THREE TABS, NAMED BY THE COACH'S QUESTIONS (his Proposal 1 + amendment, 2026-09-24):
 * **Week** (what is the week — the calendar, the tasks, next week) · **Progress** (how is it going — the
 * metrics, goals, coverage, body metrics, photos, updates) · **Profile** (who is this — their setup,
 * limitations, notes, settings). Seven tabs was their shape with ours bolted on; three named by questions
 * is a shape nobody can call theirs.
 * ⚠️ THE SEVEN OLD WORDS STILL OPEN THE RIGHT PLACE (`LEGACY_TAB`), so no pasted link breaks. */
export const CLIENT_TABS = ["week", "progress", "profile"] as const;

export type ClientTab = (typeof CLIENT_TABS)[number];

/** The tab each of the seven retired `?tab=` words now lives in — every old link still lands. */
export const LEGACY_TAB: Readonly<Record<string, ClientTab>> = {
  overview: "week",
  training: "week",
  tasks: "week",
  metrics: "progress",
  goals: "progress",
  coverage: "progress",
  settings: "profile",
};

export const TAB_QUERY_KEY = "tab";

/** The panel a bare `#/clients/<id>` opens on. */
export const DEFAULT_CLIENT_TAB: ClientTab = "week";

/**
 * The panel a link asks for.
 *
 * ⚠️ **A VALUE WE DO NOT HAVE RESOLVES TO THE DEFAULT, NEVER TO THE NEAREST THING** — the rule
 * `halfFromQuery` and `spanFromQuery` already state. Guessing that `metric` probably meant
 * `metrics` is how a pasted link shows somebody a different screen from the one that was sent.
 */
export function tabFromQuery(q: Record<string, string>): ClientTab {
  const raw = q[TAB_QUERY_KEY] ?? "";
  if ((CLIENT_TABS as readonly string[]).includes(raw)) return raw as ClientTab;
  return LEGACY_TAB[raw] ?? DEFAULT_CLIENT_TAB;
}

/**
 * THE WHOLE QUERY FOR ONE VIEW OF THIS PAGE — the panel, and whatever that panel contributes.
 *
 * ⚠️ **ONE COMPOSER FOR THE CLIENT PAGE'S ADDRESS.** `metricsQuery` learned this the hard way at
 * W30(d): the moment there are two keys the choice is between one function that knows about all
 * of them and several call sites that concatenate them their own way. There are three keys now
 * (`tab`, `half`, `exercise`) and exactly one place they are joined.
 *
 * ⚠️ **THE DEFAULT PANEL WRITES NO KEY AT ALL**, the asymmetry the half already carries: it is
 * what makes an old link, a bookmark and a bare route land on one screen instead of on three.
 * `?tab=overview` is still READ as the overview — writing nothing is not the same as refusing it.
 *
 * ⚠️ **AND A PANEL'S OWN KEYS ARE DROPPED WHEN IT IS NOT THE PANEL ON SCREEN.** `rest` is only
 * ever passed by the panel that is drawing, so an address states what is on the screen and
 * nothing else — no `half=exercise` riding along under a Settings tab.
 */
export function clientQuery(tab: ClientTab, rest: string): string {
  const keys = [
    tab === DEFAULT_CLIENT_TAB ? "" : `${TAB_QUERY_KEY}=${tab}`,
    rest,
  ].filter((k) => k !== "");
  return keys.length === 0 ? "" : `?${keys.join("&")}`;
}

/**
 * 🔴 THE DEV-ONLY `?ht-tab=` IS AN **ALIAS** FOR THE REAL KEY NOW, NOT A SECOND MECHANISM.
 *
 * It lives in `location.search` (before the `#`), was read once in a `useState` initialiser, and
 * exists so a still can be shot of a panel. Deleting it would have broken every render batch this
 * project has; leaving it as its own reader would have left TWO answers to *which panel is open*
 * with nothing to make them agree. So it folds onto `tab` here, and a ruler pins that the page
 * has one reader.
 *
 * ⚠️ **THE HASH WINS WHEN BOTH ARE PRESENT**, and that is the load-bearing half: a still is shot
 * with `?ht-tab=metrics` in the search, and the moment the batch CLICKS a tab the hash carries
 * `tab=training` while the search still says metrics. Without this precedence the click would be
 * undone by a stale alias on the next read.
 *
 * ⚠️ **AND IT IS PASSED THE SEARCH STRING RATHER THAN READING `window`** so it can be driven by a
 * ruler; the caller passes `""` outside DEV, where `import.meta.env.DEV` folds to the literal
 * `false` at build time and the whole alias disappears from the bundle.
 */
export function withDevTabAlias(q: Record<string, string>, search: string): Record<string, string> {
  if (TAB_QUERY_KEY in q) return q;
  const asked = new URLSearchParams(search).get("ht-tab");
  return asked === null ? q : { ...q, [TAB_QUERY_KEY]: asked };
}
